import { NavLink, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard,
  Receipt,
  FileText,
  Users,
  Package,
  Wallet,
  BarChart3,
  Settings,
  FileStack,
  PackageCheck,
  ShieldCheck,
  LogOut,
  Building2,
  Plug,
  LifeBuoy,
  Wallet2,
  Archive,
  ChefHat,
  Gift,
  BookOpen,
  Armchair,
  Wifi,
} from "lucide-react";
import { useAuthStore } from "../store/auth-store";
import { api } from "../lib/api";

// moduleKey: undefined = uvek vidljivo (osnovna funkcionalnost, nije
// vezano za paid modul). Kad je zadato, stavka se sakriva ako firma nema
// taj modul ACTIVE/TRIAL (isti izvor istine kao server-side EntitlementService
// - vidi GET /company/modules). Ovo je SAMO UI convenience - backend i dalje
// odbija pristup (ModuleAccessGuard) čak i da neko otvori URL ručno.
interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
  moduleKey?: string;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/", label: "Početna", icon: LayoutDashboard, end: true },
  { to: "/prodaja", label: "Prodaja", icon: FileStack },
  { to: "/fiskalizacija", label: "Fiskalizacija", icon: Receipt, moduleKey: "kasa" },
  { to: "/efakture", label: "eFakture", icon: FileText, moduleKey: "efakture" },
  { to: "/eotpremnice", label: "eOtpremnice", icon: FileText, moduleKey: "eotpremnice" },
  { to: "/kupci", label: "Kupci", icon: Users },
  { to: "/artikli", label: "Artikli i usluge", icon: Package },
  { to: "/prijem-robe", label: "Magacinsko poslovanje", icon: PackageCheck, moduleKey: "magacin" },
  { to: "/normativ", label: "Normativ", icon: ChefHat, moduleKey: "normativ" },
  { to: "/lojalti", label: "Lojalti program", icon: Gift, moduleKey: "loyalti" },
  { to: "/horeca", label: "HoReCa", icon: Armchair, moduleKey: "horeca" },
  { to: "/izvodi", label: "Izvodi", icon: Wallet, moduleKey: "bank_import" },
  { to: "/troskovi", label: "Troškovi", icon: Wallet2, moduleKey: "troskovi" },
  { to: "/e-arhiv", label: "E-arhiv", icon: Archive, moduleKey: "earhiv" },
  { to: "/wisp", label: "Internet korisnici", icon: Wifi, moduleKey: "wisp_radius" },
  { to: "/izvestaji", label: "Izveštaji", icon: BarChart3 },
  { to: "/integracije", label: "Integracije (API)", icon: Plug },
  { to: "/podrska", label: "Podrška", icon: LifeBuoy },
  { to: "/podesavanja", label: "Podešavanja", icon: Settings },
];

export function Sidebar() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const companyName = useAuthStore((s) => s.companyName);
  const logout = useAuthStore((s) => s.logout);
  const isAdmin = user?.role === "OWNER" || user?.role === "ADMIN";

  const { data: modulesResponse } = useQuery({
    queryKey: ["my-company-modules-web"],
    queryFn: () => api.getMyCompanyModulesForWeb(),
    staleTime: 5 * 60_000,
  });
  const activeModuleKeys = new Set(modulesResponse?.modules.map((m) => m.key) ?? []);

  // Kad je HoReCa aktivan, Normativ i Lojalti su tabovi unutar HoReCa (bez dupliranja u meniju).
  const inHoreca = activeModuleKeys.has("horeca");
  const visibleNavItems = NAV_ITEMS.filter(
    (item) =>
      (!item.moduleKey || activeModuleKeys.has(item.moduleKey)) &&
      !(inHoreca && (item.to === "/normativ" || item.to === "/lojalti")),
  );

  return (
    <aside className="flex h-full w-60 flex-shrink-0 flex-col bg-sidebar text-slate-100">
      <div className="flex h-14 items-center gap-2 px-5">
        <img src="/brand-logo.png" alt="HAFiskal" className="h-6 w-auto" />
      </div>

      <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto px-3 py-2" aria-label="Glavna navigacija">
        {visibleNavItems.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors ${
                isActive
                  ? "bg-sidebar-active text-white"
                  : "text-slate-300 hover:bg-sidebar-hover hover:text-white"
              }`
            }
          >
            <Icon className="h-4 w-4 flex-shrink-0" aria-hidden />
            {label}
          </NavLink>
        ))}
        {isAdmin && (
          <NavLink
            to="/admin/korisnici"
            className={({ isActive }) =>
              `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors ${
                isActive
                  ? "bg-sidebar-active text-white"
                  : "text-slate-300 hover:bg-sidebar-hover hover:text-white"
              }`
            }
          >
            <ShieldCheck className="h-4 w-4 flex-shrink-0" aria-hidden />
            Korisnici i dozvole
          </NavLink>
        )}
        {user?.isSuperAdmin && (
          <NavLink
            to="/finora-admin"
            className={({ isActive }) =>
              `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors ${
                isActive
                  ? "bg-sidebar-active text-white"
                  : "text-slate-300 hover:bg-sidebar-hover hover:text-white"
              }`
            }
          >
            <Building2 className="h-4 w-4 flex-shrink-0" aria-hidden />
            HAFiskal Admin
          </NavLink>
        )}
      </nav>

      <div className="border-t border-sidebar-border px-4 py-3">
        {user && (
          <div className="mb-2">
            <p className="truncate text-sm font-medium text-white">{user.fullName}</p>
            <p className="truncate text-xs text-slate-400">{companyName}</p>
          </div>
        )}
        <button
          onClick={() => {
            logout();
            navigate("/prijava", { replace: true });
          }}
          className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs text-slate-400 hover:bg-sidebar-hover hover:text-white"
        >
          <LogOut className="h-3.5 w-3.5" aria-hidden />
          Odjavi se
        </button>
      </div>
    </aside>
  );
}
